import { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { isAxiosError } from 'axios';
import { ImportExcelDialog } from './ImportExcelDialog';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { paths } from '@/routes/paths';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { AxiosError } from 'axios';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { formatDateShort } from '@/utils/dateFormatter';
import { datePickerLocaleText } from '@/utils/datePickerLocaleText';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import {
  useAppraisalCycles,
  useActiveAppraisalCycle,
  useCreateAppraisalCycle,
  useUpdateAppraisalCycle,
  useImportMappings,
  useStartAppraisalProcess,
  useCycleAppraisals,
  useCycleMappingsPreview,
  useUpdateCycleAppraisal,
  useDeleteCycleAppraisal,
  useBulkDeleteCycleAppraisals,
  useExportAppraiseeList,
  useSendAppraiseeReminder,
  useSendCollatorReminder,
} from '@/queries/useAppraisalCycles';
import { useActiveStaff } from '@/queries/useOCCerSearch';
import type { StaffSearchResult } from '@/api/occerSearchApi';
import { useEligibleProxies } from '@/queries/useAppraisalDetail';
import type { EligibleProxy } from '@/api/types/appraisal.types';
import { getGradeRank } from './gradeUtils';
import {
  formatAppraisalStatusLabel as formatStatusLabel,
  getAppraisalStatusChipSx as getStatusChipSx,
} from './appraisalStatusChip';
import type {
  CycleAppraisal,
  CycleAppraisalsFilterRequest,
  CreateAppraisalCycleRequest,
  UpdateCycleAppraisalRequest,
  ImportError,
} from '@/api/types/appraisalCycle.types';

dayjs.extend(isBetween);

interface ProxyAssignmentAutocompleteProps {
  appraisalId: number;
  delegateStaffId: number | null;
  currentDelegateName: string | null;
  onChange: (staffId: number | null) => void;
}

const ProxyAssignmentAutocomplete = memo(function ProxyAssignmentAutocomplete({
  appraisalId,
  delegateStaffId,
  currentDelegateName,
  onChange,
}: ProxyAssignmentAutocompleteProps) {
  const { data: eligibleProxies = [] } = useEligibleProxies(appraisalId.toString(), '');
  const options: EligibleProxy[] = delegateStaffId != null
    && !eligibleProxies.some((p) => p.staffId === delegateStaffId)
    && currentDelegateName
    ? [
        ...eligibleProxies,
        (() => {
          const parts = currentDelegateName.split(' ');
          return {
            staffId: delegateStaffId,
            firstName: parts[0] ?? '',
            lastName: parts.slice(1).join(' '),
            email: '',
            grade: '',
          };
        })(),
      ]
    : eligibleProxies;
  const value = options.find((s) => s.staffId === delegateStaffId) ?? null;
  return (
    <Autocomplete<EligibleProxy>
      size="small"
      options={options}
      getOptionLabel={(option) => option.grade
        ? `${option.firstName} ${option.lastName} (${option.grade})`
        : `${option.firstName} ${option.lastName}`}
      value={value}
      onChange={(_, newValue) => onChange(newValue?.staffId ?? null)}
      renderInput={(params) => <TextField {...params} />}
      sx={{ width: 220 }}
      isOptionEqualToValue={(option, v) => option.staffId === v.staffId}
    />
  );
});

type SortDirection = 'asc' | 'desc';

// Appraisal status options - backend enum values
const appraisalStatusOptions = ['NotStarted', 'SelfAssessment', 'CollatorReview', 'CollatorUpdates', 'TcReview', 'AwaitingHrGuidance', 'StaffingReview', 'StaffingAccepted', 'ReadyForDelivery', 'Delivered', 'DeliveryAcknowledged'];

interface AppraisalColumn {
  id: keyof CycleAppraisal | 'actions';
  label: string;
  sortable: boolean;
  filterable: boolean;
  isDateColumn?: boolean;
  minWidth?: number;
}

const appraisalColumns: AppraisalColumn[] = [
  { id: 'appraiseeName', label: 'APPRAISEE', sortable: true, filterable: true, minWidth: 150 },
  { id: 'collatorName', label: 'COLLATOR', sortable: true, filterable: true, minWidth: 150 },
  { id: 'geo', label: 'GEO', sortable: true, filterable: true, minWidth: 100 },
  { id: 'department', label: 'DEPARTMENT', sortable: true, filterable: true, minWidth: 120 },
  { id: 'status', label: 'STATUS', sortable: true, filterable: true, minWidth: 140 },
  { id: 'selfAssessmentDate', label: 'SELF-ASSESSMENT DATE', sortable: true, filterable: true, isDateColumn: true, minWidth: 200 },
  { id: 'collationDeadline', label: 'COLLATION DEADLINE', sortable: true, filterable: true, isDateColumn: true, minWidth: 190 },
  { id: 'earliestDeliveryDate', label: 'EARLIEST DELIVERY DATE', sortable: true, filterable: true, isDateColumn: true, minWidth: 220 },
  { id: 'delegateName', label: 'PROXY ASSIGNMENT', sortable: true, filterable: true, minWidth: 150 },
  { id: 'tcDate', label: 'TC DATE', sortable: true, filterable: true, isDateColumn: true, minWidth: 110 },
  { id: 'actions', label: 'ACTIONS', sortable: false, filterable: false, minWidth: 80 },
];

// Sticky column constants for pinned left/right columns
const CHECKBOX_COL_WIDTH = 48;
const STICKY_COL_SHADOW = '4px 0 8px -4px rgba(0,0,0,0.15)';
const STICKY_RIGHT_COL_SHADOW = '-4px 0 8px -4px rgba(0,0,0,0.15)';

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  // Strip trailing Z to avoid UTC-to-local timezone shift (dates are date-only values)
  return formatDateShort(dateString.replace(/Z$/i, ''));
}

// Memoized filter row for appraisals table
interface AppraisalFilterRowProps {
  showCheckbox: boolean;
  showActions: boolean;
  columns: AppraisalColumn[];
  inputValues: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
  onClearFilter: (field: string) => void;
  activeFilterCount: number;
  onClearAllFilters: () => void;
}

const AppraisalFilterRow = memo(function AppraisalFilterRow({
  showCheckbox,
  showActions,
  columns,
  inputValues,
  onFilterChange,
  onClearFilter,
  activeFilterCount,
  onClearAllFilters,
}: AppraisalFilterRowProps) {
  return (
    <TableRow>
      {showCheckbox && (
        <TableCell padding="checkbox" sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider', position: 'sticky', left: 0, zIndex: 3 }} />
      )}
      {columns
        .filter((column) => showActions || column.id !== 'actions')
        .map((column) => (
          <TableCell key={column.id} sx={{
            minWidth: column.minWidth,
            bgcolor: 'white',
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            ...(column.id === 'appraiseeName' && {
              position: 'sticky',
              left: showCheckbox ? CHECKBOX_COL_WIDTH : 0,
              zIndex: 3,
              boxShadow: STICKY_COL_SHADOW,
            }),
            ...(column.id === 'actions' && {
              position: 'sticky',
              right: 0,
              zIndex: 3,
              boxShadow: STICKY_RIGHT_COL_SHADOW,
            }),
          }}>
            {column.filterable && column.id !== 'status' && !column.isDateColumn && (
              <TextField
                size="small"
                placeholder="Type..."
                value={inputValues[column.id] ?? ''}
                onChange={(e) => onFilterChange(column.id, e.target.value)}
                InputProps={{
                  endAdornment: inputValues[column.id] ? (
                    <IconButton
                      size="small"
                      onClick={() => onClearFilter(column.id)}
                      sx={{ p: 0.5 }}
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  width: '100%',
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'divider' },
                  },
                }}
              />
            )}
            {column.filterable && column.isDateColumn && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DatePicker
                    format="DD MMM YYYY"
                    label="From"
                    value={inputValues[`${column.id}From`] ? dayjs(inputValues[`${column.id}From`]) : null}
                    onChange={(value: dayjs.Dayjs | null) => {
                      onFilterChange(`${column.id}From`, value?.format('YYYY-MM-DD') ?? '');
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: false,
                        sx: { width: 160, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem', pr: 0 }, '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiInputAdornment-root': { ml: 0 } },
                      },
                    }}
                  />
                  {(inputValues[`${column.id}From`] || inputValues[`${column.id}To`]) && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        onClearFilter(`${column.id}From`);
                        onClearFilter(`${column.id}To`);
                      }}
                      sx={{ p: 0 }}
                    >
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
                <DatePicker
                  format="DD MMM YYYY"
                  label="To"
                  value={inputValues[`${column.id}To`] ? dayjs(inputValues[`${column.id}To`]) : null}
                  onChange={(value: dayjs.Dayjs | null) => {
                    onFilterChange(`${column.id}To`, value?.format('YYYY-MM-DD') ?? '');
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: false,
                      sx: { width: 160, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem', pr: 0 }, '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiInputAdornment-root': { ml: 0 } },
                    },
                  }}
                />
              </Box>
            )}
            {column.id === 'status' && (
              <FormControl size="small" fullWidth>
                <Select<string[]>
                  multiple
                  displayEmpty
                  value={inputValues[column.id] ? inputValues[column.id].split(',') : []}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = typeof value === 'string' ? value.split(',') : value;
                    onFilterChange(column.id, selected.join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return <Typography component="span" sx={{ color: 'text.secondary' }}>Type...</Typography>;
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((s) => (
                          <Chip key={s} label={formatStatusLabel(s)} size="small" />
                        ))}
                      </Box>
                    );
                  }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  }}
                >
                  {appraisalStatusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={(inputValues[column.id] ?? '').split(',').includes(status)} size="small" />
                      {formatStatusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {column.id === 'actions' && activeFilterCount > 1 && (
              <Link
                component="button"
                variant="body2"
                onClick={onClearAllFilters}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'error.main',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all <CloseIcon sx={{ fontSize: 16 }} />
              </Link>
            )}
          </TableCell>
        ))}
    </TableRow>
  );
});

export function AppraisalAdminPage() {
  // Role-based access: Staffing = full edit, HR = read-only
  const permissions = useAuthStore((state) => state.permissions);
  const hasRole = useAuthStore((state) => state.hasRole);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);
  const canEdit = hasRole('Staffing');

  // ===== CYCLE CONFIGURATION STATE =====
  const [cycleName, setCycleName] = useState('');
  const [cycleStartDate, setCycleStartDate] = useState<dayjs.Dayjs | null>(null);
  const [cycleEndDate, setCycleEndDate] = useState<dayjs.Dayjs | null>(null);
  const [collationDeadline, setCollationDeadline] = useState<dayjs.Dayjs | null>(null);
  const [selfAppraisalDeadline, setSelfAppraisalDeadline] = useState<dayjs.Dayjs | null>(null);
  const [earliestDeliveryDate, setEarliestDeliveryDate] = useState<dayjs.Dayjs | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const [editingCycleStatus, setEditingCycleStatus] = useState<'Draft' | 'Active' | 'Closed' | null>(null);
  const [editingCycleMappingCount, setEditingCycleMappingCount] = useState<number>(0);
  const [showPreviousCycles, setShowPreviousCycles] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // ===== APPRAISALS TABLE STATE =====
  const [filters, setFilters] = useState<Partial<CycleAppraisalsFilterRequest>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<string>('appraiseeName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAppraisalId, setMenuAppraisalId] = useState<number | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingRowData, setEditingRowData] = useState<Partial<UpdateCycleAppraisalRequest> | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [menuAppraisalStatus, setMenuAppraisalStatus] = useState<string | null>(null);
  const [menuAppraiseeName, setMenuAppraiseeName] = useState('');
  const [menuCollatorOrProxyName, setMenuCollatorOrProxyName] = useState('');
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [reminderType, setReminderType] = useState<'appraisee' | 'collator' | null>(null);

  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const showNotification = useNotificationStore((state) => state.showNotification);

  // ===== QUERIES =====
  const { data: activeCycle, isLoading: isLoadingActiveCycle } = useActiveAppraisalCycle();
  const { data: allCycles } = useAppraisalCycles({ page: 1, pageSize: 100, sortBy: 'createdAt', sortDirection: 'desc' });

  // Appraisals query for active cycle
  const filterRequest: CycleAppraisalsFilterRequest = useMemo(
    () => ({
      page: page + 1,
      pageSize,
      sortBy,
      sortDirection,
      ...filters,
    }),
    [page, pageSize, sortBy, sortDirection, filters]
  );

  const { data: appraisalsData, isLoading: isLoadingAppraisals, isFetching: isFetchingAppraisals, isError: isErrorAppraisals } = useCycleAppraisals(
    activeCycle?.id ?? 0,
    filterRequest,
    !!activeCycle?.id
  );

  // Mappings preview for Draft cycles with imported mappings
  const showMappingsPreview = !activeCycle && !!editingCycleId && editingCycleMappingCount > 0;
  const { data: mappingsPreviewData } = useCycleMappingsPreview(
    editingCycleId ?? 0,
    page + 1,
    pageSize,
    showMappingsPreview
  );

  // Use appraisals data if active cycle exists, otherwise use mappings preview
  const tableData = activeCycle ? appraisalsData : mappingsPreviewData;
  const isPreviewMode = showMappingsPreview && !activeCycle;

  // ===== MUTATIONS =====
  const createCycleMutation = useCreateAppraisalCycle();
  const updateCycleMutation = useUpdateAppraisalCycle();
  const importMappingsMutation = useImportMappings();
  const startProcessMutation = useStartAppraisalProcess();
  const updateAppraisalMutation = useUpdateCycleAppraisal();
  const deleteAppraisalMutation = useDeleteCycleAppraisal();
  const bulkDeleteMutation = useBulkDeleteCycleAppraisals();
  const exportAppraiseeListMutation = useExportAppraiseeList();
  const sendAppraiseeReminderMutation = useSendAppraiseeReminder();
  const sendCollatorReminderMutation = useSendCollatorReminder();
  const { data: activeStaffData } = useActiveStaff();

  const appraisalRows = useMemo(() => tableData?.items ?? [], [tableData?.items]);
  const totalCount = tableData?.totalCount ?? 0;
  const previousCycles = useMemo(
    () => allCycles?.items ?? [],
    [allCycles?.items]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load cycle data into form on initial load
  useEffect(() => {
    // Only auto-load if we're not already editing a cycle
    if (editingCycleId !== null) return;

    const loadCycleIntoForm = (cycle: { id: number; name: string; status: string; startDate: string; endDate: string; collationDeadline: string; selfAppraisalDeadline: string; earliestDeliveryDate: string; mappingCount?: number }) => {
      setCycleName(cycle.name);
      setCycleStartDate(dayjs(cycle.startDate));
      setCycleEndDate(dayjs(cycle.endDate));
      setCollationDeadline(dayjs(cycle.collationDeadline));
      setSelfAppraisalDeadline(dayjs(cycle.selfAppraisalDeadline));
      setEarliestDeliveryDate(dayjs(cycle.earliestDeliveryDate));
      setEditingCycleId(cycle.id);
      setEditingCycleStatus(cycle.status as 'Draft' | 'Active' | 'Closed');
      setEditingCycleMappingCount(cycle.mappingCount ?? 0);
    };

    if (activeCycle) {
      // State E: active cycle is expired — check for a Draft cycle to load instead
      if (activeCycle.status === 'Active' && !dayjs().isBefore(dayjs(activeCycle.endDate), 'day')) {
        const draftCycle = allCycles?.items.find((c) => c.status === 'Draft');
        if (draftCycle) {
          loadCycleIntoForm(draftCycle);
        }
        // No Draft: leave all fields blank (State A appearance)
        return;
      }

      loadCycleIntoForm(activeCycle);
    } else if (allCycles) {
      // No active cycle — check for a Draft cycle to restore
      const draftCycle = allCycles.items.find((c) => c.status === 'Draft');
      if (draftCycle) {
        loadCycleIntoForm(draftCycle);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCycle, allCycles]);

  // ===== OVERLAP VALIDATION =====
  const [overlapError, setOverlapError] = useState<string | null>(null);

  const checkCycleOverlap = useCallback((startDate: dayjs.Dayjs | null, endDate: dayjs.Dayjs | null): string | null => {
    if (!startDate || !endDate || !allCycles?.items) return null;
    const overlapping = allCycles.items.find((cycle) => {
      // Exclude current cycle being edited
      if (cycle.id === editingCycleId) return false;
      const existingStart = dayjs(cycle.startDate);
      const existingEnd = dayjs(cycle.endDate);
      return startDate.isBefore(existingEnd, 'day') || startDate.isSame(existingEnd, 'day')
        ? (existingStart.isBefore(endDate, 'day') || existingStart.isSame(endDate, 'day'))
        : false;
    });
    if (overlapping) {
      return `Dates overlap with existing cycle "${overlapping.name}" (${formatDate(overlapping.startDate)} - ${formatDate(overlapping.endDate)}).`;
    }
    return null;
  }, [allCycles?.items, editingCycleId]);

  // Check overlap whenever dates change
  useEffect(() => {
    setOverlapError(checkCycleOverlap(cycleStartDate, cycleEndDate));
  }, [cycleStartDate, cycleEndDate, checkCycleOverlap]);

  // ===== CYCLE HANDLERS =====
  const handleSaveCycle = async () => {
    setShowSaveConfirm(false);
    if (!cycleName?.trim() || !cycleStartDate || !cycleEndDate || !collationDeadline || !selfAppraisalDeadline || !earliestDeliveryDate) {
      showNotification('Please fill in all required fields.', 'error');
      return;
    }
    if (cycleEndDate.isBefore(cycleStartDate, 'day')) {
      showNotification('End date cannot be earlier than the start date.', 'error');
      return;
    }
    if (isDeadlineBeforeStart(collationDeadline) || isDeadlineBeforeStart(selfAppraisalDeadline) || isDeadlineBeforeStart(earliestDeliveryDate)) {
      showNotification('Deadlines cannot be earlier than the cycle start date.', 'error');
      return;
    }
    if (overlapError) {
      showNotification(overlapError, 'error');
      return;
    }

    const request: CreateAppraisalCycleRequest = {
      name: cycleName,
      startDate: cycleStartDate.format('YYYY-MM-DD'),
      endDate: cycleEndDate.format('YYYY-MM-DD'),
      collationDeadline: collationDeadline.format('YYYY-MM-DD'),
      selfAppraisalDeadline: selfAppraisalDeadline.format('YYYY-MM-DD'),
      earliestDeliveryDate: earliestDeliveryDate.format('YYYY-MM-DD'),
    };

    try {
      if (editingCycleId) {
        const editingCycle = activeCycle?.id === editingCycleId
          ? activeCycle
          : allCycles?.items.find((c) => c.id === editingCycleId);
        await updateCycleMutation.mutateAsync({ id: editingCycleId, request, rowVersion: editingCycle?.rowVersion });
        showNotification('Cycle updated successfully.', 'success');
      } else {
        const newCycle = await createCycleMutation.mutateAsync(request);
        setEditingCycleId(newCycle.id);
        setEditingCycleStatus('Draft');
        setEditingCycleMappingCount(0);
        showNotification('Cycle created successfully.', 'success');
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to save cycle.';
      showNotification(apiMessage, 'error');
    }
  };

  const handleImportFile = async (file: File) => {
    if (!file || !editingCycleId) return { success: false, importedCount: 0, errors: [] };

    try {
      const result = await importMappingsMutation.mutateAsync({ cycleId: editingCycleId, file });
      if (result.success) {
        setUploadedFileName(file.name);
        setEditingCycleMappingCount(result.importedCount);
        setImportDialogOpen(false);
        showNotification(`Imported ${result.importedCount} mappings successfully.`, 'success');
      }
      return result;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; errors?: ImportError[] }>;
      const data = axiosError?.response?.data;
      // Per-row validation errors are surfaced inside the import dialog via the returned result.
      if (data?.errors?.length) {
        return { success: false, importedCount: 0, errors: data.errors };
      }
      showNotification(data?.message ?? 'Failed to import mappings.', 'error');
      return { success: false, importedCount: 0, errors: [] };
    }
  };

  const handleStartProcess = async () => {
    if (!editingCycleId) return;

    try {
      const startCycle = activeCycle?.id === editingCycleId
        ? activeCycle
        : allCycles?.items.find((c) => c.id === editingCycleId);
      const result = await startProcessMutation.mutateAsync({ cycleId: editingCycleId, rowVersion: startCycle?.rowVersion });
      setEditingCycleStatus('Active');
      setShowStartConfirm(false);
      showNotification(`Appraisal process started. ${result.appraisalsCreated} appraisals created.`, 'success');
    } catch {
      setShowStartConfirm(false);
      showNotification('Failed to start appraisal process.', 'error');
    }
  };

  // ===== APPRAISALS TABLE HANDLERS =====
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = useCallback((field: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (value === 'Invalid Date') return;
      const dateMatch = value.match(/^(\d{4})-\d{2}-\d{2}$/);
      if (dateMatch && parseInt(dateMatch[1], 10) < 1900) return;
      setFilters((prev) => ({ ...prev, [field]: value || undefined }));
      setPage(0);
    }, 300);
  }, []);

  const handleClearFilter = useCallback((field: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setInputValues((prev) => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field as keyof CycleAppraisalsFilterRequest];
      return newFilters;
    });
    setPage(0);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setInputValues({});
    setFilters({});
    setPage(0);
  }, []);

  const activeFilterCount = useMemo(() => {
    const dateColumnIds = appraisalColumns
      .filter((col) => col.isDateColumn)
      .map((col) => col.id.toString());
    const dateFromToKeys = new Set(
      dateColumnIds.flatMap((id) => [`${id}From`, `${id}To`])
    );
    // Count non-date filters normally
    let count = Object.entries(inputValues)
      .filter(([key, v]) => !dateFromToKeys.has(key) && v && v.trim() !== '')
      .length;
    // Count 1 per date column when either From or To has a value
    for (const id of dateColumnIds) {
      if ((inputValues[`${id}From`] && inputValues[`${id}From`].trim() !== '') ||
          (inputValues[`${id}To`] && inputValues[`${id}To`].trim() !== '')) {
        count++;
      }
    }
    return count;
  }, [inputValues]);

  // Wait for permissions to load before checking access
  // Redirect only if permissions are loaded AND user doesn't have required roles
  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(appraisalRows.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const isSelected = (id: number) => selectedIds.includes(id);

  // Three-dot menu handlers
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: CycleAppraisal) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuAppraisalId(row.id);
    setMenuAppraisalStatus(row.status);
    setMenuAppraiseeName(row.appraiseeName);
    setMenuCollatorOrProxyName(row.collatorName);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  // Action handlers
  const handleEditRow = (appraisalId: number) => {
    const appraisal = appraisalRows.find((r) => r.id === appraisalId);
    if (appraisal) {
      setEditingRowId(appraisalId);
      setEditingRowData({
        collatorStaffId: appraisal.collatorStaffId,
        delegateStaffId: appraisal.delegateStaffId,
        selfAssessmentDate: appraisal.selfAssessmentDate,
        collationDeadline: appraisal.collationDeadline,
        earliestDeliveryDate: appraisal.earliestDeliveryDate,
        rowVersion: appraisal.rowVersion,
        status: appraisal.status,
      });
    }
  };

  const handleDeleteClick = (appraisalId: number) => {
    setMenuAppraisalId(appraisalId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!menuAppraisalId || !activeCycle) return;
    setShowDeleteConfirm(false);
    try {
      await deleteAppraisalMutation.mutateAsync({ cycleId: activeCycle.id, appraisalId: menuAppraisalId });
      setSuccessMessage('Appraisal deleted successfully.');
      setSuccessDialogOpen(true);
      setSelectedIds((prev) => prev.filter((id) => id !== menuAppraisalId));
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to delete appraisal.';
      showNotification(apiMessage, 'error');
    }
    setMenuAppraisalId(null);
  };

  const handleReminderClick = (type: 'appraisee' | 'collator') => {
    setReminderType(type);
    setShowReminderConfirm(true);
  };

  const handleConfirmReminder = async () => {
    if (!menuAppraisalId || !activeCycle || !reminderType) return;
    setShowReminderConfirm(false);
    try {
      if (reminderType === 'appraisee') {
        await sendAppraiseeReminderMutation.mutateAsync({ cycleId: activeCycle.id, appraisalId: menuAppraisalId });
      } else {
        await sendCollatorReminderMutation.mutateAsync({ cycleId: activeCycle.id, appraisalId: menuAppraisalId });
      }
      showNotification('Reminder sent successfully.', 'success');
    } catch {
      showNotification('Failed to send reminder.', 'error');
    }
    setMenuAppraisalId(null);
    setReminderType(null);
  };

  const handleSaveEdit = async () => {
    if (!editingRowId || !editingRowData || !activeCycle) return;
    try {
      await updateAppraisalMutation.mutateAsync({
        cycleId: activeCycle.id,
        appraisalId: editingRowId,
        request: editingRowData as UpdateCycleAppraisalRequest,
      });
      setSuccessMessage('Appraisal updated successfully.');
      setSuccessDialogOpen(true);
      setEditingRowId(null);
      setEditingRowData(null);
    } catch (error) {
      // On 409 conflict, exit edit mode — the global toast handles the message
      // and the mutation onError invalidates the cache to refresh data
      if (isAxiosError(error) && error.response?.status === 409) {
        setEditingRowId(null);
        setEditingRowData(null);
        return;
      }
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to update appraisal.';
      showNotification(apiMessage, 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingRowData(null);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!activeCycle || selectedIds.length === 0) return;
    setShowBulkDeleteConfirm(false);
    try {
      const result = await bulkDeleteMutation.mutateAsync({ cycleId: activeCycle.id, appraisalIds: selectedIds });
      setSuccessMessage(`${result.successCount} appraisal${result.successCount !== 1 ? 's' : ''} deleted successfully.`);
      setSuccessDialogOpen(true);
      setSelectedIds([]);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const apiMessage = axiosError?.response?.data?.message ?? 'Failed to delete appraisals.';
      showNotification(apiMessage, 'error');
    }
  };

  const handleExportAppraiseeList = async () => {
    try {
      const blob = await exportAppraiseeListMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appraisee-list-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      showNotification('Failed to export appraisee list.', 'error');
    }
  };

  // Can start process only if:
  // 1. Editing a cycle (editingCycleId exists)
  // 2. Cycle is in Draft status (not Active or Closed)
  // 3. Cycle has mappings (either just uploaded or from before)
  const hasMappings = editingCycleMappingCount > 0 || !!uploadedFileName;
  const canStartProcess = !!editingCycleId && editingCycleStatus === 'Draft' && hasMappings;

  // Date-gating: enabled when today is within the cycle date range + 1 month extension
  const isTodayInCycleRange = cycleStartDate && cycleEndDate
    && dayjs().isBetween(cycleStartDate, cycleEndDate.add(1, 'month'), 'day', '[]');

  // Form field editability: role-based and status-based
  // null = creating new cycle — editable without date check
  // 'Draft' = saved but not started — fields remain editable regardless of date range
  // Save button is separately date-gated via isTodayInCycleRange
  const isFormEditable = canEdit && (editingCycleStatus === null || editingCycleStatus === 'Draft');

  const isImportDisabled = !editingCycleId || editingCycleStatus !== 'Draft' || !isTodayInCycleRange;

  // Appraisals table edit/delete (Staffing only) is gated to the cycle window + 1 month (AC1).
  // Must use activeCycle.endDate rather than form-state cycleEndDate, which is blanked out
  // in State E (expired Active cycle). Backend enforces the same rule in AppraisalCycleEditability.
  const isActiveCycleInEditWindow = !!activeCycle
    && !dayjs().isAfter(dayjs(activeCycle.endDate).add(1, 'month'), 'day');
  const canEditAppraisals = canEdit && isActiveCycleInEditWindow;

  const isDeadlineBeforeStart = (deadline: dayjs.Dayjs | null) =>
    deadline && cycleStartDate && deadline.isBefore(cycleStartDate, 'day');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={datePickerLocaleText}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          APPRAISAL ADMIN
        </Typography>

        <Section>
          <SectionHeader>APPRAISALS CYCLE DATES</SectionHeader>
          <SectionCard>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Box sx={{ flex: 5, minWidth: 500 }}>
                <TextField
                  label="Cycle name"
                  value={cycleName}
                  onChange={(e) => setCycleName(e.target.value.slice(0, 25))}
                  fullWidth
                  size="small"
                  disabled={!isFormEditable}
                  error={showValidation && !cycleName.trim()}
                  helperText={`${cycleName.length}/25`}
                  slotProps={{ htmlInput: { maxLength: 25 } }}
                  sx={{ mb: 3 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Appraisal cycle
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 1 }}>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={cycleStartDate}
                      onChange={(value) => setCycleStartDate(value)}
                      disabled={!isFormEditable}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: showValidation && !cycleStartDate } }}
                    />
                    <Typography>-</Typography>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={cycleEndDate}
                      onChange={(value) => setCycleEndDate(value)}
                      disabled={!isFormEditable}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: showValidation && !cycleEndDate } }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Collation Deadline
                    </Typography>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={collationDeadline}
                      onChange={(value) => setCollationDeadline(value)}
                      disabled={!isFormEditable}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: (showValidation && !collationDeadline) || !!isDeadlineBeforeStart(collationDeadline), helperText: isDeadlineBeforeStart(collationDeadline) ? 'Cannot be before cycle start' : undefined } }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Self-assessment Deadline
                    </Typography>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={selfAppraisalDeadline}
                      onChange={(value) => setSelfAppraisalDeadline(value)}
                      disabled={!isFormEditable}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: (showValidation && !selfAppraisalDeadline) || !!isDeadlineBeforeStart(selfAppraisalDeadline), helperText: isDeadlineBeforeStart(selfAppraisalDeadline) ? 'Cannot be before cycle start' : undefined } }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Earliest delivery date
                    </Typography>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={earliestDeliveryDate}
                      onChange={(value) => setEarliestDeliveryDate(value)}
                      disabled={!isFormEditable}
                      slotProps={{ textField: { size: 'small', fullWidth: true, error: (showValidation && !earliestDeliveryDate) || !!isDeadlineBeforeStart(earliestDeliveryDate), helperText: isDeadlineBeforeStart(earliestDeliveryDate) ? 'Cannot be before cycle start' : undefined } }}
                    />
                  </Box>
                </Box>

                {overlapError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {overlapError}
                  </Alert>
                )}

                {canEdit && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="secondary" sx={{ height: 40 }} onClick={() => {
                      setShowValidation(true);
                      if (!cycleName?.trim() || !cycleStartDate || !cycleEndDate || !collationDeadline || !selfAppraisalDeadline || !earliestDeliveryDate) {
                        showNotification('Please fill in all required fields.', 'error');
                        return;
                      }
                      if (isDeadlineBeforeStart(collationDeadline) || isDeadlineBeforeStart(selfAppraisalDeadline) || isDeadlineBeforeStart(earliestDeliveryDate)) {
                        showNotification('Deadlines cannot be earlier than the cycle start date.', 'error');
                        return;
                      }
                      setShowSaveConfirm(true);
                    }} disabled={!isFormEditable || !isTodayInCycleRange}>
                      Save
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: 5, minWidth: 280, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    onClick={() => setShowPreviousCycles(!showPreviousCycles)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: 1 }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPreviousCycles(!showPreviousCycles); } }}
                    aria-expanded={showPreviousCycles}
                  >
                    {showPreviousCycles ? <ExpandLess /> : <ExpandMore />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Current and Previous Cycles
                    </Typography>
                  </Box>
                  <Collapse in={showPreviousCycles} timeout="auto" unmountOnExit>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {previousCycles.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No previous cycles
                        </Typography>
                      ) : (
                        previousCycles.map((cycle) => (
                          <Box key={cycle.id} sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {cycle.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(cycle.startDate)} - {formatDate(cycle.endDate)}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Collapse>
                </Box>

                {canEdit && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Tooltip
                      title={uploadedFileName ?? ''}
                      disableHoverListener={!uploadedFileName}
                      disableFocusListener={!uploadedFileName}
                      disableTouchListener={!uploadedFileName}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setImportDialogOpen(true)}
                        disabled={isImportDisabled}
                        sx={{ flex: 1, height: 40, minWidth: 0 }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {uploadedFileName ?? 'IMPORT EXCEL'}
                        </Box>
                      </Button>
                    </Tooltip>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setShowStartConfirm(true)}
                      disabled={!canStartProcess}
                      sx={{ flex: 1, height: 40, minWidth: 0 }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        START APPRAISAL PROCESS
                      </Box>
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </SectionCard>
        </Section>

        <Section>
          <SectionHeader>APPRAISALS</SectionHeader>
          <SectionCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {activeCycle ? `${formatDate(activeCycle.startDate)} - ${formatDate(activeCycle.endDate)}` : isPreviewMode ? 'Imported mappings preview (Draft)' : 'No active cycle'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {canEdit && (
                  <Typography variant="body2" color="text.secondary">
                    Actions for selected ({selectedIds.length}) appraisals:
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAppraiseeList}
                >
                  EXPORT APPRAISEE LIST
                </Button>
                {canEdit && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    disabled={selectedIds.length === 0}
                  >
                    DELETE
                  </Button>
                )}
              </Box>
            </Box>

            {isLoadingActiveCycle || isLoadingAppraisals ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : isErrorAppraisals ? (
              <Alert severity="error" sx={{ m: 2 }}>
                Failed to load appraisals.
              </Alert>
            ) : !activeCycle && !isPreviewMode ? (
              <EmptyStateMessage message="No active appraisal cycle. Create and start a cycle to see appraisals." />
            ) : (
              <>
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {canEdit && (
                          <TableCell padding="checkbox" sx={{ bgcolor: 'custom.deepTeal', color: 'white', position: 'sticky', left: 0, zIndex: 4 }}>
                            <Checkbox
                              indeterminate={selectedIds.length > 0 && selectedIds.length < appraisalRows.length}
                              checked={appraisalRows.length > 0 && selectedIds.length === appraisalRows.length}
                              onChange={handleSelectAll}
                              sx={{ color: 'white', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'white' } }}
                            />
                          </TableCell>
                        )}
                        {appraisalColumns
                          .filter((column) => canEdit || column.id !== 'actions')
                          .map((column) => (
                            <TableCell
                              key={column.id}
                              sx={{
                                minWidth: column.minWidth,
                                bgcolor: 'custom.deepTeal',
                                color: 'white',
                                ...(column.id === 'appraiseeName' && {
                                  position: 'sticky',
                                  left: canEdit ? CHECKBOX_COL_WIDTH : 0,
                                  zIndex: 4,
                                  boxShadow: STICKY_COL_SHADOW,
                                }),
                                ...(column.id === 'actions' && {
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 4,
                                  boxShadow: STICKY_RIGHT_COL_SHADOW,
                                }),
                              }}
                            >
                              {column.sortable ? (
                                <TableSortLabel
                                  active={sortBy === column.id}
                                  direction={sortBy === column.id ? sortDirection : 'asc'}
                                  onClick={() => handleSort(column.id)}
                                >
                                  {column.label}
                                </TableSortLabel>
                              ) : (
                                column.label
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                      <AppraisalFilterRow
                        showCheckbox={canEdit}
                        showActions={canEdit}
                        columns={appraisalColumns}
                        inputValues={inputValues}
                        onFilterChange={handleFilterChange}
                        onClearFilter={handleClearFilter}
                        activeFilterCount={activeFilterCount}
                        onClearAllFilters={handleClearAllFilters}
                      />
                    </TableHead>
                    <TableBody sx={{ opacity: isFetchingAppraisals ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                      {appraisalRows.map((row) => {
                        const isItemSelected = isSelected(row.id);
                        const isEditing = editingRowId === row.id;
                        return (
                          <TableRow key={row.id} hover selected={isItemSelected}>
                            {canEdit && (
                              <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, zIndex: 1, '&&&': { backgroundColor: 'background.paper' } }}>
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={() => handleSelectOne(row.id)}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ position: 'sticky', left: canEdit ? CHECKBOX_COL_WIDTH : 0, zIndex: 1, boxShadow: STICKY_COL_SHADOW, '&&&': { backgroundColor: 'background.paper' } }}>
                              <Link component={RouterLink} to={`/appraisals/${row.id}`} underline="hover">
                                {row.appraiseeName}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <Autocomplete<StaffSearchResult>
                                  size="small"
                                  options={(activeStaffData ?? []).filter(
                                    (staff) => getGradeRank(staff.billingGrade) > getGradeRank(row.billingGrade)
                                  )}
                                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.billingGrade})`}
                                  value={(activeStaffData ?? []).find((s) => s.employeeId === editingRowData?.collatorStaffId) ?? null}
                                  onChange={(_, newValue) => {
                                    setEditingRowData((prev) => ({ ...prev, collatorStaffId: newValue?.employeeId ?? 0 }));
                                  }}
                                  renderInput={(params) => <TextField {...params} />}
                                  sx={{ width: 220 }}
                                  isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
                                />
                              ) : (
                                row.collatorName
                              )}
                            </TableCell>
                            <TableCell>{row.geo || '-'}</TableCell>
                            <TableCell>{row.department || '-'}</TableCell>
                            <TableCell>
                              {isEditing ? (
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value={editingRowData?.status ?? row.status}
                                    onChange={(e: SelectChangeEvent) =>
                                      setEditingRowData((prev) => ({ ...prev, status: e.target.value }))
                                    }
                                  >
                                    {appraisalStatusOptions.map((status) => (
                                      <MenuItem key={status} value={status}>
                                        {formatStatusLabel(status)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <Chip
                                  label={formatStatusLabel(row.status)}
                                  size="small"
                                  sx={{ minWidth: 100, justifyContent: 'center', ...getStatusChipSx(row.status) }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <DatePicker
                                  format="DD MMM YYYY"
                                  value={editingRowData?.selfAssessmentDate ? dayjs(editingRowData.selfAssessmentDate) : null}
                                  onChange={(value) =>
                                    setEditingRowData((prev) => ({
                                      ...prev,
                                      selfAssessmentDate: value?.format('YYYY-MM-DD') ?? null,
                                    }))
                                  }
                                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                />
                              ) : (
                                formatDate(row.selfAssessmentDate)
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <DatePicker
                                  format="DD MMM YYYY"
                                  value={editingRowData?.collationDeadline ? dayjs(editingRowData.collationDeadline) : null}
                                  onChange={(value) =>
                                    setEditingRowData((prev) => ({
                                      ...prev,
                                      collationDeadline: value?.format('YYYY-MM-DD') ?? null,
                                    }))
                                  }
                                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                />
                              ) : (
                                formatDate(row.collationDeadline)
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <DatePicker
                                  format="DD MMM YYYY"
                                  value={editingRowData?.earliestDeliveryDate ? dayjs(editingRowData.earliestDeliveryDate) : null}
                                  onChange={(value) =>
                                    setEditingRowData((prev) => ({
                                      ...prev,
                                      earliestDeliveryDate: value?.format('YYYY-MM-DD') ?? null,
                                    }))
                                  }
                                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                />
                              ) : (
                                formatDate(row.earliestDeliveryDate)
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <ProxyAssignmentAutocomplete
                                  appraisalId={row.id}
                                  delegateStaffId={editingRowData?.delegateStaffId ?? null}
                                  currentDelegateName={row.delegateName}
                                  onChange={(staffId) =>
                                    setEditingRowData((prev) => ({ ...prev, delegateStaffId: staffId }))
                                  }
                                />
                              ) : (
                                row.delegateName ?? '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(row.tcDate)}
                            </TableCell>
                            {canEdit && (
                              <TableCell sx={{ position: 'sticky', right: 0, zIndex: 1, boxShadow: STICKY_RIGHT_COL_SHADOW, '&&&': { backgroundColor: 'background.paper' } }}>
                                {isEditing ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={handleSaveEdit}
                                      disabled={updateAppraisalMutation.isPending}
                                      sx={{ color: 'success.main' }}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                      disabled={updateAppraisalMutation.isPending}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Tooltip title="Actions">
                                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {appraisalRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={canEdit ? appraisalColumns.length + 1 : appraisalColumns.length - 1} align="center">
                            <EmptyStateMessage message="No appraisals found." />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </SectionCard>
        </Section>

        {canEdit && selectedIds.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              zIndex: 1000,
            }}
          >
            <IconButton size="small" onClick={() => setSelectedIds([])}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 500, mr: 2 }}>
              {selectedIds.length} items selected
            </Typography>
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setShowBulkDeleteConfirm(true)}
              sx={{ color: 'error.main', textTransform: 'uppercase', fontSize: '0.75rem' }}
            >
              Delete
            </Button>
          </Paper>
        )}

        <ImportExcelDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleImportFile}
          isImporting={importMappingsMutation.isPending}
        />

        <Dialog open={showSaveConfirm} onClose={() => setShowSaveConfirm(false)}>
          <DialogTitle>Save Appraisal Cycle</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Save appraisal cycle configuration?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSaveConfirm(false)}>Cancel</Button>
            <Button onClick={handleSaveCycle} variant="contained" color="secondary">Confirm</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showStartConfirm}
          onClose={() => { if (!startProcessMutation.isPending) setShowStartConfirm(false); }}
          disableEscapeKeyDown={startProcessMutation.isPending}
        >
          <DialogTitle>Start Appraisal Process</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to start the appraisal process for the cycle &apos;{cycleName}&apos;? This will create all appraisal forms and send notifications to appraisees and collators. This action cannot be undone.
            </DialogContentText>
            {startProcessMutation.isPending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Starting appraisal process... This may take up to a minute. Please do not close this window.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStartConfirm(false)} disabled={startProcessMutation.isPending}>Cancel</Button>
            <Button
              onClick={handleStartProcess}
              variant="contained"
              color="secondary"
              disabled={startProcessMutation.isPending}
              startIcon={startProcessMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              START PROCESS
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>Delete Appraisal</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete this appraisal? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showBulkDeleteConfirm} onClose={() => setShowBulkDeleteConfirm(false)}>
          <DialogTitle>Delete Appraisals</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete {selectedIds.length} appraisal{selectedIds.length !== 1 ? 's' : ''}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleBulkDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showReminderConfirm} onClose={() => setShowReminderConfirm(false)}>
          <DialogTitle>Send Reminder</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Send a reminder to {reminderType === 'appraisee' ? menuAppraiseeName : menuCollatorOrProxyName}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowReminderConfirm(false)}>Cancel</Button>
            <Button onClick={handleConfirmReminder} variant="contained" color="secondary">Send Reminder</Button>
          </DialogActions>
        </Dialog>

        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
          {canEditAppraisals && (
            <MenuItem onClick={() => { handleEditRow(menuAppraisalId!); handleCloseMenu(); }}>Edit</MenuItem>
          )}
          {menuAppraisalStatus === 'SelfAssessment' && (
            <MenuItem onClick={() => { handleReminderClick('appraisee'); handleCloseMenu(); }}>Send Reminder to Appraisee</MenuItem>
          )}
          {(menuAppraisalStatus === 'CollatorReview' || menuAppraisalStatus === 'CollatorUpdates') && (
            <MenuItem onClick={() => { handleReminderClick('collator'); handleCloseMenu(); }}>Send Reminder to Collator</MenuItem>
          )}
          <MenuItem onClick={() => { handleDeleteClick(menuAppraisalId!); handleCloseMenu(); }}>Delete</MenuItem>
        </Menu>

        <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)}>
          <DialogTitle>Success</DialogTitle>
          <DialogContent>
            <DialogContentText>{successMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuccessDialogOpen(false)} variant="contained" color="secondary">OK</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </LocalizationProvider>
  );
}

export default AppraisalAdminPage;
